import React, { useState, useContext, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import AuthModal from './AuthModal/AuthModal';
import eventEmitter from '../Home/eventEmitter';
import Header from './Header';
import Hero from './Hero';
import TrustBar from './TrustBar';
import CategoryStrip from './CategoryStrip';
import HowItWorks from './HowItWorks';
import OrderOptions from './OrderOptions';
import ContentBottom from './ContentBottom';
import Newsletter from './Newsletter';
import Footer from './Footer';
import styles from './LandingPageV4.module.css';
import { ContextSearch } from '../../Context/SearchContext';

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.authUser);
  const { fetchCategoryProducts } = useContext(ContextSearch);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalTab, setAuthModalTab] = useState('signin');
  const [pendingStoreType, setPendingStoreType] = useState('');
  const [pendingCategoryName, setPendingCategoryName] = useState('');

  const handleOpenAuthModal = (tab = 'signin', storeType = '') => {
    eventEmitter.emit('openAuthModal');
    setAuthModalTab(tab);
    setPendingStoreType(storeType);
    if (storeType) setPendingCategoryName('');
    setShowAuthModal(true);
  };

  const handleCloseAuthModal = () => {
    setShowAuthModal(false);
    setPendingStoreType('');
    setPendingCategoryName('');
  };

  const handleSignInClick = () => {
    if (isAuthenticated) {
      navigate('/home', { replace: false });
      return;
    }
    handleOpenAuthModal('signin');
  };

  const handleSignUpClick = () => {
    if (isAuthenticated) {
      navigate('/home', { replace: false });
      return;
    }
    handleOpenAuthModal('signup');
  };

  const handleLandingCta = () => {
    setPendingCategoryName('');
    if (isAuthenticated) {
      navigate('/home', { replace: false });
      return;
    }
    handleOpenAuthModal('signin');
  };

  const navigateToCategoryAfterLogin = useCallback(
    async (categoryName) => {
      try {
        const responseData = await fetchCategoryProducts(categoryName);
        const smartsearch =
          responseData?.smartsearch || responseData?.message?.smartsearch;
        if (smartsearch) {
          navigate(`/category/${smartsearch}?category=${smartsearch}`);
        } else {
          navigate('/home', { replace: false });
        }
      } catch {
        navigate('/home', { replace: false });
      }
    },
    [fetchCategoryProducts, navigate],
  );

  const handleCategoryNeedAuth = (categoryName) => {
    setPendingStoreType('');
    setPendingCategoryName(categoryName || '');
    if (isAuthenticated) {
      navigate('/home', { replace: false });
      setPendingCategoryName('');
      return;
    }
    handleOpenAuthModal('signin');
  };

  const hasUserLocation = () => {
    const stored = Cookies.get('userLocation');
    if (!stored) return false;
    try {
      const loc = JSON.parse(decodeURIComponent(stored));
      return loc?.lat && loc?.lng;
    } catch {
      return false;
    }
  };

  const handleOrderClick = (storeType) => {
    setPendingCategoryName('');
    if (hasUserLocation()) {
      navigate(`/home?store=${storeType}`, { replace: false });
      return;
    }

    handleOpenAuthModal('signin', storeType);
  };

  return (
    <div className={styles.pageWrapper}>
      <AuthModal
        isOpen={showAuthModal}
        onClose={handleCloseAuthModal}
        initialTab={authModalTab}
        postLoginStoreType={pendingStoreType}
        postLoginCategoryName={pendingCategoryName}
        onPostLoginCategoryNavigate={navigateToCategoryAfterLogin}
      />
      <Header
        onSignInClick={handleSignInClick}
        onSignUpClick={handleSignUpClick}
      />
      <main id="main-content">
        <div className={styles.heroViewport}>
          <Hero
            onSignInClick={handleLandingCta}
            isAuthenticated={isAuthenticated}
          />
          <TrustBar />
        </div>
        <CategoryStrip onNeedAuth={handleCategoryNeedAuth} />
        <HowItWorks />
        <OrderOptions
          onDeliveryClick={() => handleOrderClick('delivery')}
          onPickupClick={() => handleOrderClick('pickup')}
        />
        <ContentBottom />
        <Newsletter />
      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;
